import React, { useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from './ui/button';
import { Copy, Download, MessageCircle, Check, Share2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function buildShareText(cagnotte) {
  const isYako = cagnotte.type === 'Yako';
  const emoji = isYako ? '🕊️' : '🎉';
  const label = isYako ? 'Cagnotte Yako' : 'Cagnotte Joyeuse';
  return `${emoji} ${label} · Fodindin\n\n"${cagnotte.title}"\n\nSoutenez cette cagnotte sur Fodindin :`;
}

export default function ShareCard({ cagnotte, compact = false }) {
  const { toast } = useToast();
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Two URLs:
  // - `prettyUrl` is displayed in the UI (clean, humain-friendly SPA route)
  // - `shareUrl` targets the backend `/api/share/{id}` endpoint which serves Open Graph
  //   metadata to WhatsApp/Facebook scrapers and instantly redirects real browsers to the SPA.
  const prettyUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/cagnotte/${cagnotte.id}`;
  }, [cagnotte.id]);

  const shareUrl = useMemo(() => {
    const base = BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/api/share/${cagnotte.id}`;
  }, [cagnotte.id]);

  const isYako = cagnotte.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';

  const qrImageSettings = useMemo(() => ({
    src: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><rect width='40' height='40' rx='10' fill='${accent}'/><text x='50%' y='55%' font-family='Inter,Arial' font-size='24' font-weight='800' text-anchor='middle' fill='#fff'>F</text></svg>`),
    height: 34,
    width: 34,
    excavate: true,
  }), [accent]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(prettyUrl);
      setCopied(true);
      toast({ title: 'Lien copié !', description: prettyUrl });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast({ title: 'Impossible de copier', description: 'Copiez le lien manuellement.' });
    }
  };

  const shareWhatsApp = () => {
    const text = `${buildShareText(cagnotte)}\n${shareUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Fodindin — ${cagnotte.title}`, text: buildShareText(cagnotte), url: shareUrl });
      } catch (e) {
        // User cancelled the native share sheet, or the browser rejected the payload. Non-fatal.
        if (process.env.NODE_ENV === 'development') console.info('[ShareCard] navigator.share cancelled', e?.name || e);
      }
    } else {
      copyLink();
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `fodindin-cagnotte-${cagnotte.id}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className={`rounded-3xl bg-white border border-black/5 ${compact ? 'p-5' : 'p-6 sm:p-8'} card-lift`}>
      <div className="flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-semibold" style={{ color: accent }}>
        <Share2 className="w-3.5 h-3.5" /> Partager la cagnotte
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-6 items-center">
        <div ref={qrRef} className="rounded-2xl bg-white p-3 border border-black/5 shadow-sm shrink-0">
          <QRCodeCanvas
            value={shareUrl}
            size={compact ? 160 : 200}
            level="H"
            includeMargin={false}
            fgColor="#1a1d1a"
            bgColor="#ffffff"
            imageSettings={qrImageSettings}
          />
        </div>
        <div className="flex-1 w-full space-y-3">
          <div className="text-[13px] text-neutral-600">Scannez le QR ou partagez le lien avec vos proches. Chaque don compte.</div>
          <div className="flex items-center gap-2 rounded-full bg-neutral-50 border border-black/5 px-3 py-2 text-[12.5px] text-neutral-700">
            <span className="truncate flex-1">{prettyUrl}</span>
            <button onClick={copyLink} className="shrink-0 text-[12px] font-medium text-neutral-700 hover:text-black flex items-center gap-1">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copié' : 'Copier'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={shareWhatsApp} className="btn-anim h-10 rounded-full bg-[#25D366] hover:bg-[#1fbb59] text-white text-[13px] font-medium px-4">
              <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
            </Button>
            <Button onClick={shareNative} variant="outline" className="btn-anim h-10 rounded-full bg-white border-black/10 text-[13px] px-4">
              <Share2 className="w-4 h-4 mr-1.5" /> Partager
            </Button>
            <Button onClick={downloadQR} variant="outline" className="btn-anim h-10 rounded-full bg-white border-black/10 text-[13px] px-4">
              <Download className="w-4 h-4 mr-1.5" /> QR Code
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
